import RefundCollectionSummary from '../components/RefundCollectionSummary';
import React, { useState } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const REPORTS = [
  { key:'sales-performance', label:'Sales Performance', icon:'📈', desc:'Revenue by sales rep, collection rates, top customers' },
  { key:'aging', label:'Receivables Aging', icon:'⏱', desc:'Outstanding invoices by age — 0-30, 31-60, 61-90, 90+ days' },
  { key:'customers', label:'Customer Analysis', icon:'👥', desc:'Lifetime value, new vs returning, at-risk customers' },
  { key:'cashflow', label:'Cash Flow', icon:'💰', desc:'Money in vs money out, bank balances, forecasting' },
  { key:'supplier-spend', label:'Supplier Spend', icon:'🏭', desc:'Spend analysis per supplier, monthly trends' },
];

export default function AdvancedReports() {
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState('');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0];
  });
  const [to, setTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user?.role !== 'super_admin') {
    return (
      <div className="empty-state">
        <h3>Access Restricted</h3>
        <p>Advanced Reports are only available to the Super Admin.</p>
      </div>
    );
  }

  const runReport = async () => {
    if (!activeReport) return;
    setLoading(true); setError(''); setData(null);
    try {
      const needsDates = activeReport !== 'aging';
      const qs = needsDates ? '?from='+from+'&to='+to : '';
      const result = await get('/advanced-reports/'+activeReport+qs);
      setData(result);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const setQuickRange = (type) => {
    const d = new Date();
    switch(type) {
      case 'this_month':
        setFrom(new Date(d.getFullYear(),d.getMonth(),1).toISOString().split('T')[0]);
        setTo(d.toISOString().split('T')[0]); break;
      case 'last_month':
        setFrom(new Date(d.getFullYear(),d.getMonth()-1,1).toISOString().split('T')[0]);
        setTo(new Date(d.getFullYear(),d.getMonth(),0).toISOString().split('T')[0]); break;
      case 'this_year':
        setFrom(d.getFullYear()+'-01-01');
        setTo(d.toISOString().split('T')[0]); break;
      case 'last_6':
        setFrom(new Date(d.getFullYear(),d.getMonth()-5,1).toISOString().split('T')[0]);
        setTo(d.toISOString().split('T')[0]); break;
    }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Advanced Reports</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Deep business performance analysis</p>
        </div>
        {data && <button className="btn btn-outline" onClick={()=>window.print()}>Print Report</button>}
      </div>

      {/* Report selector cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:20}}>
        {REPORTS.map(r => (
          <div key={r.key}
            style={{background:'#fff',border:'2px solid '+(activeReport===r.key?'#556B2F':'var(--border-light)'),
              borderRadius:10,padding:'14px 16px',cursor:'pointer',transition:'all .15s',
              boxShadow:activeReport===r.key?'0 2px 8px rgba(85,107,47,.2)':'var(--shadow)'}}
            onClick={() => { setActiveReport(r.key); setData(null); }}>
            <div style={{fontSize:24,marginBottom:8}}>{r.icon}</div>
            <div style={{fontWeight:600,fontSize:13,color:'#3B2A21',marginBottom:4}}>{r.label}</div>
            <div style={{fontSize:11,color:'#8A8A8A',lineHeight:1.4}}>{r.desc}</div>
          </div>
        ))}
      </div>

      {activeReport && (
        <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:20}}>
          {activeReport !== 'aging' && (
            <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end',marginBottom:12}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">From Date</label>
                <input type="date" className="form-control" value={from} onChange={e=>setFrom(e.target.value)} />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">To Date</label>
                <input type="date" className="form-control" value={to} onChange={e=>setTo(e.target.value)} />
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {[['This Month','this_month'],['Last Month','last_month'],['Last 6 Months','last_6'],['This Year','this_year']].map(([label,key])=>(
                  <button key={key} className="btn btn-ghost btn-sm" onClick={()=>setQuickRange(key)}>{label}</button>
                ))}
              </div>
            </div>
          )}
          <button className="btn btn-primary" onClick={runReport} disabled={loading}>
            {loading ? 'Running...' : 'Run Report'}
          </button>
        </div>
      )}

      {error && <div className="alert alert-error mb-md">{error}</div>}

      {data && (
        <div id="report-output">
          {activeReport==='sales-performance' && <SalesPerformanceReport data={data} from={from} to={to} />}
          {activeReport==='aging' && <AgingReport data={data} />}
          {activeReport==='customers' && <CustomerAnalysisReport data={data} from={from} to={to} />}
          {activeReport==='cashflow' && <CashFlowReport data={data} from={from} to={to} />}
          {activeReport==='supplier-spend' && <SupplierSpendReport data={data} from={from} to={to} />}
        </div>
      )}
    </div>
  );
}

const ReportHeader = ({ title, from, to }) => (
  <div style={{background:'#556B2F',padding:'16px 24px',borderRadius:'10px 10px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:0}}>
    <div>
      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:'#A8893D'}}>{title}</div>
      <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2}}>Bloans Creations Ltd</div>
    </div>
    {from && to && (
      <div style={{textAlign:'right',fontSize:12,color:'rgba(242,239,231,.6)'}}>
        <div>Period: {from} to {to}</div>
        <div style={{marginTop:2}}>Generated: {new Date().toLocaleDateString('en-NG')}</div>
      </div>
    )}
  </div>
);

const SummaryTile = ({ label, value, color, sub }) => (
  <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'14px 16px'}}>
    <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:6}}>{label}</div>
    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:color||'#3B2A21'}}>{value}</div>
    {sub && <div style={{fontSize:11,color:'#8A8A8A',marginTop:3}}>{sub}</div>}
  </div>
);

function SalesPerformanceReport({ data, from, to }) {
  const { by_rep, monthly_trend, top_customers } = data;
  const totalRevenue = by_rep.reduce((s,r)=>s+parseFloat(r.total_collected||0),0);

  // Build chart data - one bar per month, grouped by rep
  const months = [...new Set(monthly_trend.map(r=>r.month))];
  const reps = [...new Set(monthly_trend.map(r=>r.rep_name))];
  const chartData = months.map(month => {
    const obj = { month };
    reps.forEach(rep => {
      const found = monthly_trend.find(r=>r.month===month && r.rep_name===rep);
      obj[rep] = found ? parseFloat(found.collected) : 0;
    });
    return obj;
  });

  const COLORS = ['#556B2F','#A8893D','#1E4FAD','#8F2E24','#2E6B3A'];

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Sales Performance Report" from={from} to={to} />
      <div style={{padding:20}}>
        {/* Rep summary cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:20}}>
          {by_rep.map((rep,i) => (
            <div key={rep.rep_name} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',boxShadow:'var(--shadow)'}}>
              <div style={{background:COLORS[i%COLORS.length],padding:'10px 14px'}}>
                <div style={{fontWeight:600,fontSize:13,color:'#F2EFE7'}}>{rep.rep_name}</div>
                <div style={{fontSize:10,color:'rgba(242,239,231,.5)',textTransform:'capitalize'}}>{rep.role?.replace('_',' ')}</div>
              </div>
              <div style={{padding:'12px 14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Invoices</span>
                  <span style={{fontWeight:600}}>{rep.invoice_count}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Billed</span>
                  <span>{fmt(rep.total_billed)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Collected</span>
                  <span style={{color:'var(--paid)',fontWeight:600}}>{fmt(rep.total_collected)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Collection Rate</span>
                  <span style={{color:parseFloat(rep.collection_rate)>=80?'var(--paid)':'var(--part)',fontWeight:600}}>{rep.collection_rate}%</span>
                </div>
                <div style={{marginTop:8,height:4,background:'#F2EFE7',borderRadius:2}}>
                  <div style={{height:'100%',borderRadius:2,background:COLORS[i%COLORS.length],width:Math.min(100,parseFloat(rep.collection_rate)||0)+'%'}}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div style={{background:'#F2EFE7',borderRadius:8,padding:'14px 18px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>Total Revenue Collected</span>
          <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',fontWeight:700,color:'#556B2F'}}>{fmt(totalRevenue)}</span>
        </div>

        {/* Monthly trend chart */}
        {chartData.length > 1 && (
          <div className="card" style={{marginBottom:20}}>
            <div className="card-hdr"><span className="card-title">Monthly Performance by Rep</span></div>
            <div style={{padding:16}}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Legend />
                  {reps.map((rep,i) => (
                    <Bar key={rep} dataKey={rep} fill={COLORS[i%COLORS.length]} radius={[3,3,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top customers */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Top Customers</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Customer','Sales Rep','Orders','Total Billed','Total Paid'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {top_customers.map((c,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.customer}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.rep_name}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.orders}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(c.total_billed)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'var(--paid)',fontWeight:600}}>{fmt(c.total_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AgingReport({ data }) {
  const { invoices, summary, by_customer } = data;
  const buckets = [
    { label:'0-30 Days', value:parseFloat(summary.bucket_0_30), color:'#2E6B3A', bg:'#E8F5EB' },
    { label:'31-60 Days', value:parseFloat(summary.bucket_31_60), color:'#8B5E0A', bg:'#FFF3DC' },
    { label:'61-90 Days', value:parseFloat(summary.bucket_61_90), color:'#8F2E24', bg:'#FDEAEA' },
    { label:'90+ Days', value:parseFloat(summary.bucket_90_plus), color:'#5C0A0A', bg:'#FFE4E4' },
  ];

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Receivables Aging Report" />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Outstanding" value={fmt(summary.total_outstanding)} color="#8F2E24" />
          {buckets.map(b => (
            <div key={b.label} style={{background:b.bg,borderRadius:8,padding:'12px 14px',border:'1px solid '+b.color+'33'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:b.color,marginBottom:6}}>{b.label}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:b.color}}>{fmt(b.value)}</div>
            </div>
          ))}
        </div>

        {/* By customer */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Top Customers by Amount Owing</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Customer','Phone','Invoices','Total Owing','Oldest Invoice'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {by_customer.map((c,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.customer}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.phone||'—'}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.invoice_count}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24',fontWeight:600}}>{fmt(c.total_owing)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:parseInt(c.oldest_invoice_days)>90?'#8F2E24':parseInt(c.oldest_invoice_days)>60?'#8B5E0A':'#555',fontWeight:500}}>{c.oldest_invoice_days} days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* All invoices */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>All Outstanding Invoices</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#F2EFE7'}}>
              {['Invoice','Date','Customer','Total','Paid','Balance','Age','Bucket'].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {invoices.map((inv,i) => (
                <tr key={i} style={{background:inv.aging_bucket==='90+ days'?'#FFF0F0':i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{inv.invoice_number}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{inv.date}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{inv.customer_name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(inv.total)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)'}}>{fmt(inv.amount_paid)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#8F2E24',fontWeight:600}}>{fmt(inv.balance_due)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{inv.days_outstanding} days</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                      background:inv.aging_bucket==='0-30 days'?'#E8F5EB':inv.aging_bucket==='31-60 days'?'#FFF3DC':inv.aging_bucket==='61-90 days'?'#FDEAEA':'#FFE4E4',
                      color:inv.aging_bucket==='0-30 days'?'#2E6B3A':inv.aging_bucket==='31-60 days'?'#8B5E0A':inv.aging_bucket==='61-90 days'?'#8F2E24':'#5C0A0A'}}>
                      {inv.aging_bucket}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustomerAnalysisReport({ data, from, to }) {
  const { lifetime_value, new_vs_returning, at_risk } = data;
  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Customer Analysis Report" from={from} to={to} />
      <div style={{padding:20}}>
        {/* Summary */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="New Customers" value={new_vs_returning?.new_customers||0} color="#1E4FAD" />
          <SummaryTile label="Returning Customers" value={new_vs_returning?.returning_customers||0} color="#556B2F" />
          <SummaryTile label="Avg Lifetime Value" value={fmt(new_vs_returning?.avg_lifetime_value||0)} color="#A8893D" />
        </div>

        {/* At Risk */}
        {at_risk.length > 0 && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8F2E24',marginBottom:10}}>⚠ At-Risk Customers (No Order in 60+ Days)</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'#8F2E24'}}>
                {['Customer','Phone','Pipeline Stage','Last Order','Days Inactive','Total Orders','Lifetime Value'].map(h=>(
                  <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {at_risk.map((c,i) => (
                  <tr key={i} style={{background:i%2===0?'#fff':'#FFF8F8'}}>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.name}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.phone||'—'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textTransform:'capitalize'}}>{(c.pipeline_stage||'').replace('_',' ')}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{c.last_order||'Never'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24',fontWeight:600}}>{c.days_inactive||'—'} days</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.total_orders}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#556B2F'}}>{fmt(c.lifetime_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Lifetime value table */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Customer Lifetime Value</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Customer','Phone','Orders','Lifetime Value','Total Paid','First Order','Last Order','Days Since'].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {lifetime_value.map((c,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.phone||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.total_orders}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#556B2F',fontWeight:600}}>{fmt(c.lifetime_value)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)'}}>{fmt(c.total_paid)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{c.first_order||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{c.last_order||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:parseInt(c.days_since_last_order)>90?'#8F2E24':parseInt(c.days_since_last_order)>60?'#8B5E0A':'inherit'}}>{c.days_since_last_order||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CashFlowReport({ data, from, to }) {
  const { monthly, bank_balances, outstanding } = data;
  const totalIn = monthly.reduce((s,m)=>s+m.money_in,0);
  const totalOut = monthly.reduce((s,m)=>s+m.money_out,0);
  const totalBankBalance = bank_balances.reduce((s,b)=>s+parseFloat(b.balance||0),0);

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Cash Flow Report" from={from} to={to} />
      <div style={{padding:20}}>
        <RefundCollectionSummary collections={data.customer_collections} unallocated={data.unallocated_refunds} />
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Money In" value={fmt(totalIn)} color="#556B2F" />
          <SummaryTile label="Total Money Out" value={fmt(totalOut)} color="#8F2E24" />
          <SummaryTile label="Refunds (included in money out)" value={fmt(monthly.reduce((sum,m)=>sum+Number(m.refunds_out||0),0))} color="#8F2E24" />
          <SummaryTile label="Net Cash Flow" value={fmt(totalIn-totalOut)} color={totalIn>=totalOut?'#2E6B3A':'#8F2E24'} />
          <SummaryTile label="Total Bank Balance" value={fmt(totalBankBalance)} color="#1E4FAD" />
        </div>

        {/* Outstanding */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
          <div style={{background:'#E8F5EB',borderRadius:8,padding:'14px 16px',border:'1px solid #B8DEC0'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#2E6B3A',marginBottom:6}}>Outstanding Receivables (Money Coming In)</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#2E6B3A'}}>{fmt(outstanding?.receivables)}</div>
          </div>
          <div style={{background:'#FDEAEA',borderRadius:8,padding:'14px 16px',border:'1px solid #F5B8B8'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8F2E24',marginBottom:6}}>Outstanding Payables (Money Going Out)</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#8F2E24'}}>{fmt(outstanding?.payables)}</div>
          </div>
        </div>

        {/* Chart */}
        {monthly.length > 0 && (
          <div className="card" style={{marginBottom:20}}>
            <div className="card-hdr"><span className="card-title">Monthly Cash Flow</span></div>
            <div style={{padding:16}}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthly}>
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Legend />
                  <Bar dataKey="money_in" name="Money In" fill="#556B2F" radius={[3,3,0,0]} />
                  <Bar dataKey="money_out" name="Money Out" fill="#8F2E24" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Bank balances */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Bank Account Balances</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
            {bank_balances.map(b => (
              <div key={b.name} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
                <div style={{fontWeight:600,fontSize:13,color:'#3B2A21',marginBottom:2}}>{b.name}</div>
                <div style={{fontSize:11,color:'#8A8A8A',marginBottom:8}}>{b.bank_name} | {b.account_number}</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:parseFloat(b.balance)>=0?'#556B2F':'#8F2E24'}}>{fmt(b.balance)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly breakdown table */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Monthly Breakdown</div>
          <div style={{overflowX:'auto'}}><table style={{width:'100%',minWidth:980,borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Month','Money In','Bills Paid','Expenses','Refunds','Other Outflows','Total Out','Net'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {monthly.map((m,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{m.month}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'var(--paid)'}}>{fmt(m.money_in)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(m.bills_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(m.expenses_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(m.refunds_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(m.other_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24',fontWeight:600}}>{fmt(m.money_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:700,color:m.net>=0?'#556B2F':'#8F2E24'}}>{fmt(m.net)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

function SupplierSpendReport({ data, from, to }) {
  const { by_supplier, monthly_trend } = data;
  const totalSpent = by_supplier.reduce((s,r)=>s+parseFloat(r.total_paid||0),0);
  const totalOwing = by_supplier.reduce((s,r)=>s+parseFloat(r.total_owing||0),0);

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Supplier Spend Analysis" from={from} to={to} />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Suppliers" value={by_supplier.length} />
          <SummaryTile label="Total Spent" value={fmt(totalSpent)} color="#8F2E24" />
          <SummaryTile label="Total Owing" value={fmt(totalOwing)} color="#8B5E0A" />
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Spend by Supplier</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Supplier','Category','Bills','Total Billed','Total Paid','Owing'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {by_supplier.map((s,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{s.name}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{s.category||'—'}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{s.bill_count}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(s.total_billed)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24',fontWeight:600}}>{fmt(s.total_paid)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:parseFloat(s.total_owing)>0?'#8B5E0A':'var(--paid)'}}>{fmt(s.total_owing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
